import type { DashboardCellValue, DashboardFieldType, DashboardTone } from '@catalyst-psa/shared';

import { formatDate, formatDateTime } from '../../utils/date.js';

const MONEY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

const NUMBER = new Intl.NumberFormat('en-US');

/** Renders one cell value for display. Empty values show an em dash. */
export function formatCell(value: DashboardCellValue, type: DashboardFieldType): string {
  if (value === null || value === undefined || value === '') return '—';
  switch (type) {
    case 'boolean':
      return value ? 'Yes' : 'No';
    case 'date':
      return formatDate(String(value));
    case 'datetime':
      return formatDateTime(String(value));
    case 'money':
      return MONEY.format(Number(value));
    case 'hours': {
      const hours = Number(value);
      return Number.isFinite(hours) ? `${hours.toFixed(2)} hrs` : String(value);
    }
    case 'number': {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? NUMBER.format(numeric) : String(value);
    }
    default:
      return String(value);
  }
}

/** Renders an aggregate value (metric number, bar value) without an em-dash fallback. */
export function formatMeasure(value: string, type: DashboardFieldType): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  if (type === 'money') return MONEY.format(numeric);
  if (type === 'hours') return `${numeric.toFixed(2)}`;
  return NUMBER.format(numeric);
}

/** Tailwind text colour for a metric tone. */
export const TONE_TEXT: Record<DashboardTone, string> = {
  neutral: 'text-slate-900',
  green: 'text-emerald-600',
  amber: 'text-amber-600',
  red: 'text-rose-600',
  blue: 'text-brand-600',
};

/** Tailwind bar fill for a metric/series tone. */
export const TONE_BAR: Record<DashboardTone, string> = {
  neutral: 'bg-brand-500',
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-rose-500',
  blue: 'bg-brand-500',
};
